import type { MboxHistoryProductView, MboxHistoryView } from "./api";
import type { MboxHistoryLoadState } from "./useMboxHistory";
import { InlineHint } from "./InlineHint";
import {
  availabilityLabel,
  brandName,
  confidenceName,
  formatMoment,
  formatMoney,
  hazardChipLabel,
  hazardStatusLabel,
} from "./mbox-display";

/**
 * Product-dimension reference from the historical mail archive, embedded in the product card.
 *
 * Read-only and secondary by design: the block states its own data status, every value keeps its
 * date, and nothing here feeds back into CRM records. Customer-dimension archive facts live in
 * the customer profile section instead (`CustomerHistoryBlock`).
 */
export function MboxHistorySection({
  state,
  onReload,
}: {
  state: MboxHistoryLoadState;
  onReload: () => void;
}) {
  if (state.status === "UNSUPPORTED") return null;
  if (state.status === "READY" && state.view.status === "DISABLED") return null;

  const archiveProducts =
    state.status === "READY" && state.view.status !== "UNAVAILABLE"
      ? state.view.products
      : [];
  return (
    <section
      className="real-product-archive-summary"
      aria-label="产品历史邮件参考"
    >
      <header>
        <strong>产品历史邮件参考</strong>
        <span className="real-mbox-source">
          mbox 历史库
          <InlineHint label="历史邮件资料来源" text="内部资料，未经人工复核" />
        </span>
        <button type="button" className="real-evidence-link" onClick={onReload}>
          重新查询
        </button>
      </header>
      {state.status === "LOADING" ? (
        <p className="real-mbox-note">正在查询历史邮件库…</p>
      ) : state.status === "FAILED" ? (
        <p className="real-mbox-note is-muted">
          历史邮件库暂时无法查询，不影响本次分诊。
        </p>
      ) : (
        <MboxHistoryBody
          view={state.view}
          hideProductIdentity={archiveProducts.length === 1}
        />
      )}
    </section>
  );
}

function MboxHistoryBody({
  view,
  hideProductIdentity,
}: {
  view: MboxHistoryView;
  hideProductIdentity: boolean;
}) {
  if (view.status === "UNAVAILABLE") {
    return (
      <p className="real-mbox-note is-muted">
        {view.notes.join(" ") || "历史邮件库暂时不可用，本次未取得历史资料。"}
      </p>
    );
  }
  const unmatched = view.unmappedProductQueries.filter(
    (query) => query.status === "OK",
  );
  return (
    <div className="real-mbox-body">
      {view.status === "PARTIAL" ? (
        <p className="real-mbox-note is-warning">
          部分历史查询失败，以下为已取得的部分资料。
        </p>
      ) : null}
      {view.products.map((product) => (
        <MboxProductCard
          key={product.productKey}
          product={product}
          // 产品名称与 CAS 就在这张产品卡片的上方，同一个产品不再重复显示。
          hideIdentity={hideProductIdentity && product.match === "CAS"}
        />
      ))}
      {view.products.length === 0 ? (
        <p className="real-mbox-note is-muted">
          历史邮件库未匹配到当前询单产品。
          {view.unmappedProductQueries.length > 0
            ? "已按 CAS 与产品名精确查询，无命中。"
            : ""}
        </p>
      ) : null}
      {unmatched.length > 0 && view.products.length > 0 ? (
        <p className="real-mbox-note is-muted">
          以下产品在历史邮件库中没有记录：
          {unmatched
            .map((query) => query.cas ?? query.name ?? "未提供标识")
            .join("、")}
        </p>
      ) : null}
    </div>
  );
}

function MboxProductCard({
  product,
  hideIdentity,
}: {
  product: MboxHistoryProductView;
  hideIdentity: boolean;
}) {
  const latest = product.sourcing.latest;
  const previous = product.sourcing.previous;
  const latestQuote = product.quotes.recent[0] ?? null;
  const hazardNeedsAttention =
    product.hazard.everClassifiedDangerous ||
    product.hazard.hazardStatus === "DANGEROUS" ||
    product.hazard.hazardStatus === "CONFLICTED";
  return (
    <article className="real-mbox-card" aria-label="产品历史邮件参考">
      {hideIdentity ? (
        <p className="real-mbox-meta">
          {product.bid ? <code>BenchChem {product.bid}</code> : null}
          <span className="real-mbox-chip is-good">CAS 精确匹配</span>
        </p>
      ) : (
        <header className="real-mbox-card-header">
          <h3>{product.productName ?? product.cas ?? product.productKey}</h3>
          <p className="real-mbox-meta">
            {product.cas ? <code>CAS {product.cas}</code> : "CAS 未记录"}
            {product.bid ? <code>BenchChem {product.bid}</code> : null}
            <span
              className={`real-mbox-chip is-${product.match === "CAS" ? "good" : "warn"}`}
            >
              {product.match === "CAS" ? "CAS 精确匹配" : "仅产品名匹配"}
            </span>
          </p>
        </header>
      )}
      {product.match === "NAME" ? (
        <p className="real-mbox-note is-warning">
          本次仅按产品名匹配到历史记录，请人工确认与当前询单是同一产品。
        </p>
      ) : null}

      <div className="real-mbox-stats">
        <div className="real-mbox-stat">
          <span className="real-mbox-stat__label">历史询单</span>
          <strong className="real-mbox-stat__value">
            {product.inquiry.historicalCount}
            <em>次</em>
          </strong>
          <small className="real-mbox-stat__note">
            最近 {formatMoment(product.inquiry.latestAt)}
            <br />
            上次 {formatMoment(product.inquiry.previousAt)}
          </small>
        </div>
        <div className="real-mbox-stat">
          <span className="real-mbox-stat__label">询单客户</span>
          <strong className="real-mbox-stat__value">
            {product.inquiry.uniqueCustomerCount}
            <em>家</em>
          </strong>
          <small className="real-mbox-stat__note">
            {product.inquiry.uniqueCustomerDomainCount} 个域名
            <br />
            首次 {formatMoment(product.inquiry.firstAt)}
          </small>
        </div>
        <div className="real-mbox-stat">
          <span className="real-mbox-stat__label">上次查货</span>
          <strong className="real-mbox-stat__value">
            {!latest ? (
              "无记录"
            ) : (
              <span
                className={`real-mbox-chip is-${latest.sourceFound ? "good" : "warn"}`}
              >
                {latest.sourceFound ? "找到货源" : "未找到货源"}
              </span>
            )}
          </strong>
          <small className="real-mbox-stat__note">
            {latest
              ? `${formatMoment(latest.sentAt)} · ${brandName(latest.brand)} · 置信度${confidenceName(latest.confidence)}`
              : "历史邮件中没有该产品的查货结论"}
          </small>
        </div>
        <div className="real-mbox-stat">
          <span className="real-mbox-stat__label">历史货源</span>
          <strong className="real-mbox-stat__value">
            <span
              className={`real-mbox-chip is-${product.sourcing.everSourceFound ? "good" : "muted"}`}
            >
              {product.sourcing.everSourceFound ? "找到过" : "从未找到"}
            </span>
          </strong>
          <small className="real-mbox-stat__note">
            查货 {product.sourcing.attemptCount} 次
            {previous ? ` · 上一次 ${formatMoment(previous.sentAt)}` : ""}
            {product.sourcing.pendingReviewCount > 0
              ? ` · ${product.sourcing.pendingReviewCount} 条待复核`
              : ""}
          </small>
        </div>
      </div>

      <section className="real-mbox-block" aria-label="我方历史报价">
        <h4 className="real-mbox-block__title">
          我方历史报价
          {product.quotes.historicalQuoteCount > 0 ? (
            <small>共 {product.quotes.historicalQuoteCount} 次</small>
          ) : null}
        </h4>
        {latestQuote ? (
          <>
            <p className="real-mbox-block__lead">
              {latestQuote.quoteNo ? <code>{latestQuote.quoteNo}</code> : null}
              <span>{formatMoment(latestQuote.quotedAt)}</span>
            </p>
            <ul className="real-mbox-offers">
              {latestQuote.offers.length > 0 ? (
                latestQuote.offers.map((offer, index) => (
                  <li key={`${offer.size ?? "size"}-${index}`}>
                    <span>{offer.size ?? "规格未记录"}</span>
                    <strong>
                      {formatMoney(offer.currency, offer.unitPrice)}
                    </strong>
                  </li>
                ))
              ) : (
                <li>
                  <span>报价明细未记录</span>
                </li>
              )}
            </ul>
            <p className="real-mbox-stat__note">
              {brandName(latestQuote.brand)} · 客户{" "}
              {latestQuote.customerDomain ??
                latestQuote.customerEmail ??
                "未记录"}
              {latestQuote.salespersonName
                ? ` · 销售 ${latestQuote.salespersonName}`
                : ""}
            </p>
          </>
        ) : (
          <p className="real-mbox-note is-muted">
            历史邮件中没有该产品的报价记录。
          </p>
        )}
      </section>

      {product.supplierChecks.recent.length > 0 ? (
        <section className="real-mbox-block" aria-label="历史供应商查货">
          <h4 className="real-mbox-block__title">
            历史供应商查货
            <small>
              {product.supplierChecks.historicalCheckCount} 条记录 ·{" "}
              {product.supplierChecks.namedSupplierCount} 家供应商
            </small>
          </h4>
          <div className="real-mbox-suppliers">
            {product.supplierChecks.recent.map((check, index) => (
              <article
                className="real-mbox-supplier-row"
                key={`${check.supplierName ?? check.supplierAlias ?? "supplier"}-${check.checkAt ?? index}`}
              >
                <header>
                  <strong>
                    {check.supplierName ??
                      check.supplierAlias ??
                      "未命名供应商"}
                  </strong>
                  <span
                    className={`real-mbox-chip is-${
                      check.availability === "AVAILABLE"
                        ? "good"
                        : check.availability === "NOT_AVAILABLE"
                          ? "danger"
                          : "muted"
                    }`}
                  >
                    {check.availability
                      ? (availabilityLabel[check.availability] ??
                        check.availability)
                      : "供货状态未说明"}
                  </span>
                  {check.purityValues.length > 0 ? (
                    <span className="real-mbox-chip is-info">
                      纯度 {check.purityValues.join("/")}
                    </span>
                  ) : null}
                  {check.leadTimeValues.length > 0 ? (
                    <span className="real-mbox-chip is-info">
                      交期 {check.leadTimeValues.join("/")}
                    </span>
                  ) : null}
                </header>
                <ul className="real-mbox-offers">
                  {check.offers.length > 0 ? (
                    check.offers.slice(0, 4).map((offer, offerIndex) => (
                      <li key={`${offer.size ?? "size"}-${offerIndex}`}>
                        <span>{offer.size ?? "规格未记录"}</span>
                        <strong>
                          {formatMoney(offer.currency, offer.price)}
                        </strong>
                      </li>
                    ))
                  ) : (
                    <li>
                      <span>未记录价格</span>
                    </li>
                  )}
                </ul>
                <p className="real-mbox-stat__note">
                  {formatMoment(check.checkAt)}
                  {check.checkerName ? ` · 查货人 ${check.checkerName}` : ""}
                </p>
              </article>
            ))}
          </div>
        </section>
      ) : null}

      {latest?.evidenceSnippet ? (
        <details className="real-mbox-evidence">
          <summary>查看上次查货原文片段</summary>
          <p>{latest.evidenceSnippet}</p>
        </details>
      ) : null}

      <p className="real-mbox-hazard-line">
        <span className="real-mbox-stat__label">危险品历史</span>
        <span
          className={`real-mbox-chip is-${hazardNeedsAttention ? "danger" : "muted"}`}
        >
          {hazardChipLabel[product.hazard.hazardStatus] ??
            product.hazard.hazardStatus}
        </span>
        <span
          className={`real-mbox-stat__note${hazardNeedsAttention ? " is-warning" : ""}`}
        >
          {hazardStatusLabel[product.hazard.hazardStatus] ??
            product.hazard.hazardStatus}
        </span>
      </p>
    </article>
  );
}
